import { CircularProgress, Grid, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { isArray, isEmpty, keys, reduce, values } from 'es-toolkit/compat';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';

import { runSecondaryAnalysis as runSecondaryAnalysisApiCall } from 'api/platform';
import {
  CalculateFeaturesJob,
  getSecondaryAnalysisJobPrimaryRunOrchestrationId,
  JobNotification,
  SecondaryAnalysisSubmissionParams,
} from 'interfaces/job';
import { normalizeKeysToCamelCase } from 'utils/objectCase';
import queryClient from 'utils/queryClient';
import { SNACK_BAR_KEY_SECONDARY_ANALYSIS } from './useRunSecondaryAnalysis';

type SecondaryAnalysisRebuildSourceJob = Pick<
  CalculateFeaturesJob,
  'studyId' | 'primaryRunOrchestrationId' | 'params' | 'manifest'
>;

export const normalizeSecondaryAnalysisRebuildManifest = (sourceJob: SecondaryAnalysisRebuildSourceJob) => {
  const manifestFromParams = sourceJob?.params?.manifest;
  const rawManifest = !isEmpty(manifestFromParams) ? manifestFromParams : sourceJob?.manifest;

  if (!isEmpty(rawManifest) && !isArray(rawManifest)) {
    console.debug(
      'Manifest appears to be in expected format for secondary analysis rebuild, using manifest as is for rebuild',
      { rawManifest }
    );
    return rawManifest as Record<string, string[]>;
  }

  let computedManifestFromRaw: Record<string, string[]> = {};
  if (isArray(rawManifest)) {
    console.debug(
      'Manifest appears to be in `CalculateFeaturesManifestSubTask` format, computing manifest by case and slide from raw manifest for secondary analysis rebuild',
      { rawManifest }
    );
    computedManifestFromRaw = reduce(
      rawManifest,
      (manifestByCase, manifestItem) => {
        const normalizedManifestItem = normalizeKeysToCamelCase(manifestItem) as {
          caseId?: string;
          slides?: Record<string, string> | string[];
        };
        const caseId = normalizedManifestItem?.caseId;
        const slideIds = values(normalizedManifestItem?.slides) as string[];

        if (!caseId || isEmpty(slideIds)) {
          return manifestByCase;
        }

        manifestByCase[caseId] = slideIds;
        return manifestByCase;
      },
      {} as Record<string, string[]>
    ); // Initial value with type assertion since reduce can't infer the type here

    console.debug('Computed manifest from raw manifest for secondary analysis rebuild', {
      computedManifestFromRaw,
    });
  }

  if (isEmpty(computedManifestFromRaw)) {
    console.warn(
      'Computed manifest from raw is empty',
      { rawManifest },
      'falling back to using secondary analysis polygons to determine manifest for rebuild'
    );

    const secondaryAnalysisPolygons = sourceJob?.params?.secondaryAnalysisPolygons;

    if (isEmpty(secondaryAnalysisPolygons)) {
      console.error('Missing secondaryAnalysisPolygons while trying to recover empty manifest during rebuild', {
        sourceJob,
        rawManifest,
      });
      return computedManifestFromRaw;
    }

    computedManifestFromRaw = reduce(
      secondaryAnalysisPolygons,
      (manifestByCase, polygonsBySlide, caseId) => {
        const slideIds = keys(polygonsBySlide || {}) as string[];

        if (isEmpty(slideIds)) {
          return manifestByCase;
        }

        manifestByCase[caseId] = slideIds;
        return manifestByCase;
      },
      {} as Record<string, string[]>
    );

    if (isEmpty(computedManifestFromRaw)) {
      console.error('Unable to derive manifest from secondaryAnalysisPolygons', {
        secondaryAnalysisPolygons,
      });
    }
  }

  return computedManifestFromRaw as Record<string, string[]>;
};

export const mapSecondaryAnalysisRebuildPayload = ({
  sourceJob,
  jobName,
  jobDescription,
  notifications = [],
}: {
  sourceJob: SecondaryAnalysisRebuildSourceJob;
  jobName: string;
  jobDescription: string;
  notifications?: JobNotification[];
}): {
  jobName: string;
  jobDescription: string;
  notifications: JobNotification[];
  params: SecondaryAnalysisSubmissionParams;
} => {
  const primaryRunOrchestrationId = getSecondaryAnalysisJobPrimaryRunOrchestrationId(sourceJob);
  const manifest = normalizeSecondaryAnalysisRebuildManifest(sourceJob);
  const secondaryAnalysisPolygons = sourceJob?.params?.secondaryAnalysisPolygons;
  const studyId = sourceJob?.studyId || sourceJob?.params?.studyId;

  if (isEmpty(manifest)) {
    console.error('Missing manifest for secondary analysis rebuild', {
      sourceJob,
      manifest,
      secondaryAnalysisPolygons,
      manifestFromParams: sourceJob?.params?.manifest,
      manifestFromRoot: sourceJob?.manifest,
    });
    throw new Error('Missing manifest for secondary analysis rebuild');
  }

  if (!primaryRunOrchestrationId) {
    throw new Error('Missing primaryRunOrchestrationId for secondary analysis rebuild');
  }

  if (isEmpty(secondaryAnalysisPolygons)) {
    throw new Error('Missing secondaryAnalysisPolygons for secondary analysis rebuild');
  }

  if (!studyId) {
    throw new Error('Missing studyId for secondary analysis rebuild');
  }

  return {
    jobName,
    jobDescription,
    notifications,
    params: {
      studyId,
      manifest,
      primaryRunOrchestrationId,
      secondaryAnalysisPolygons,
    },
  };
};

export const useRunSecondaryAnalysisRebuild = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const runSecondaryAnalysisMutation = useMutation(runSecondaryAnalysisApiCall, {
    mutationKey: ['runSecondaryAnalysisRebuild'],
    onError: (error, params) => {
      enqueueSnackbar('Error occurred, Secondary Analysis rebuild failed', { variant: 'error' });
      console.error('Secondary Analysis rebuild failed', { error, params });
    },
    onSuccess: () => {
      enqueueSnackbar('Secondary Analysis Started', { variant: 'success' });
      queryClient.invalidateQueries(['jobs']);
      onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries(['jobs']);
      closeSnackbar(SNACK_BAR_KEY_SECONDARY_ANALYSIS);
    },
  });

  const runSecondaryAnalysisRebuild = useCallback(
    ({
      sourceJob,
      jobName,
      jobDescription,
      notifications,
    }: {
      sourceJob: SecondaryAnalysisRebuildSourceJob;
      jobName: string;
      jobDescription: string;
      notifications?: JobNotification[];
    }) => {
      let mutateParams;
      try {
        mutateParams = mapSecondaryAnalysisRebuildPayload({
          sourceJob,
          jobName,
          jobDescription,
          notifications,
        });
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Secondary Analysis rebuild failed', {
          variant: 'error',
        });
        return;
      }

      runSecondaryAnalysisMutation.mutate(mutateParams);

      enqueueSnackbar({
        variant: 'success',
        message: (
          <Grid container>
            <Grid item>
              <Typography>Waiting for Secondary Analysis to start</Typography>
            </Grid>
            <Grid item>
              <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
            </Grid>
          </Grid>
        ),
        key: SNACK_BAR_KEY_SECONDARY_ANALYSIS,
        autoHideDuration: null,
      });
    },
    [enqueueSnackbar, runSecondaryAnalysisMutation]
  );

  return {
    runSecondaryAnalysisRebuild,
    isStartingSecondaryAnalysisRebuild: runSecondaryAnalysisMutation.isLoading,
  };
};
